import { KeywordDefinition } from 'ajv';
import striptags from 'striptags';

const RICH_TEXT_FIELDS = [
  'title',
  'englishTitle',
  'subtitle',
  'englishSubtitle',
  'abstract',
  'englishAbstract'
] as const;

const countWords = (text: string): number => {
  const plain = striptags(text.replace(/<br\s*\/?>/gi, ' '))
    .replace(/&nbsp;/gi, ' ')
    .trim();
  if (plain.length === 0) return 0;
  return plain.split(/\s+/).length;
};

function validateRichTextMinLength(schema: number, data: string): boolean {
  const plain = striptags(data).trim();
  const valid = plain.length >= schema;
  (validateRichTextMinLength as any).errors = valid
    ? null
    : [
        {
          keyword: 'richTextMinLength',
          schemaPath: '#/richTextMinLength',
          params: { limit: schema },
          message: `must NOT have fewer than ${schema} characters`
        }
      ];
  return valid;
}
(validateRichTextMinLength as any).errors = null;

function validateRichTextMaxLength(schema: number, data: string): boolean {
  const plain = striptags(data).trim();
  const valid = plain.length <= schema;
  (validateRichTextMaxLength as any).errors = valid
    ? null
    : [
        {
          keyword: 'richTextMaxLength',
          schemaPath: '#/richTextMaxLength',
          params: { limit: schema },
          message: `must NOT have more than ${schema} characters`
        }
      ];
  return valid;
}
(validateRichTextMaxLength as any).errors = null;

function validateMinWords(schema: number, data: string): boolean {
  const wordCount = countWords(data);
  const valid = wordCount >= schema;
  (validateMinWords as any).errors = valid
    ? null
    : [
        {
          keyword: 'minWords',
          schemaPath: '#/minWords',
          params: { limit: schema },
          message: `must have at least ${schema} words`
        }
      ];
  return valid;
}
(validateMinWords as any).errors = null;

function validateMaxWords(schema: number, data: string): boolean {
  const wordCount = countWords(data);
  const valid = wordCount <= schema;
  (validateMaxWords as any).errors = valid
    ? null
    : [
        {
          keyword: 'maxWords',
          schemaPath: '#/maxWords',
          params: { limit: schema },
          message: `must NOT have more than ${schema} words`
        }
      ];
  return valid;
}
(validateMaxWords as any).errors = null;

export const richTextMinLengthKeyword: KeywordDefinition = {
  keyword: 'richTextMinLength',
  type: 'string',
  schemaType: 'number',
  validate: validateRichTextMinLength
};

export const richTextMaxLengthKeyword: KeywordDefinition = {
  keyword: 'richTextMaxLength',
  type: 'string',
  schemaType: 'number',
  validate: validateRichTextMaxLength
};

export const richTextMinWordsKeyword: KeywordDefinition = {
  keyword: 'minWords',
  type: 'string',
  schemaType: 'number',
  validate: validateMinWords
};

export const richTextMaxWordsKeyword: KeywordDefinition = {
  keyword: 'maxWords',
  type: 'string',
  schemaType: 'number',
  validate: validateMaxWords
};

export const RICH_TEXT_AJV_KEYWORDS = [
  richTextMinLengthKeyword,
  richTextMaxLengthKeyword,
  richTextMinWordsKeyword,
  richTextMaxWordsKeyword
];

function patchRichTextField(fieldSchema: any): void {
  if (!fieldSchema) return;
  if ('minLength' in fieldSchema) {
    fieldSchema.richTextMinLength = fieldSchema.minLength;
    delete fieldSchema.minLength;
  }
  if ('maxLength' in fieldSchema) {
    fieldSchema.richTextMaxLength = fieldSchema.maxLength;
    delete fieldSchema.maxLength;
  }
}

/**
 * Post-processes a generated AJV schema to replace minLength/maxLength on
 * richtext fields (title, englishTitle, subtitle, englishSubtitle, abstract,
 * englishAbstract) with custom keywords that strip HTML before checking length.
 */
export function applyRichTextSchemaKeywords(schema: any): any {
  const clone = JSON.parse(JSON.stringify(schema));

  // Patch top-level metadata property definitions
  const metadataProps = clone?.properties?.metadata?.properties;
  if (metadataProps) {
    for (const field of RICH_TEXT_FIELDS) {
      patchRichTextField(metadataProps[field]);
    }
  }

  // Patch allOf conditional schemas (e.g. englishTitle/englishAbstract required for Norwegian/Swedish/Danish)
  if (Array.isArray(clone.allOf)) {
    for (const entry of clone.allOf) {
      const thenMetadataProps = entry?.then?.properties?.metadata?.properties;
      if (thenMetadataProps) {
        for (const field of RICH_TEXT_FIELDS) {
          patchRichTextField(thenMetadataProps[field]);
        }
      }
    }
  }

  return clone;
}
